import { RoundWeightField } from "./RoundWeightField";
import { TeamAffiliationPicker } from "./TeamAffiliationPicker";
import { TeamLogoUpload } from "./TeamLogoUpload";
import { percentageBasisPoints } from "./roundWeightPercentage";
import { UnitLifecycleFields } from "./UnitLifecycleFields";
import { UnitDetailsFields, hiddenUnitMetadataFields } from "./UnitDetailsFields";
import { PublicationReadiness } from "./PublicationReadiness";
import { PublicationRoundPicker } from "./PublicationRoundPicker";
import { ResultComputationProgress } from "./ResultComputationProgress";
import { ApiError } from "../api/client";
import { ArrowLeftIcon, ArrowPathIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import { ValidatedForm } from "../components/ValidatedForm";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocale } from "../i18n";
import { ErrorNotice } from "../components/AdminFeedback";
import { Fields, type FieldValidation } from "./Fields";
import {
  boundFields,
  eventRequest,
  operation,
  prepare,
  queryDefaults,
  querySchema,
} from "./api";
import { clean, policyIssues, validate } from "./validation";
import { teamFieldErrors } from "./teamFieldErrors";
import { RoleAccountPicker } from "./RoleAccountPicker";
import { label, unitDetailsLabels, valueLabel } from "./labels";
import type { Context, Page, RecordValue, Unit } from "./types";
export function CommandForm({
  name,
  title,
  context,
  actorId,
  onChanged,
  onCancel,
  onTask,
  onLock,
  focusRequest,
  fixedQuery,
  fixedBody,
}: {
  name: string;
  title: string;
  context: Context;
  actorId: string;
  onChanged: (result: RecordValue) => void;
  onCancel?: () => void;
  onTask?: (tab: string, read?: string, write?: string) => void;
  onLock?: (locked: boolean) => void;
  focusRequest?: { field: string; attempt: number };
  fixedQuery?: RecordValue;
  fixedBody?: RecordValue;
}) {
  const { w, lang } = useLocale();
  const op = operation(name, true);
  const [query, setQuery] = useState<RecordValue>(() =>
    ({ ...queryDefaults(op, context), ...fixedQuery }),
  );
  const [body, setBody] = useState<RecordValue>({});
  const [savedBody, setSavedBody] = useState<RecordValue>({});
  const [roundWeight, setRoundWeight] = useState("");
  const [roundWeightValid, setRoundWeightValid] = useState(false);
  const [roundWeightLocked, setRoundWeightLocked] = useState(false);
  const [roundWeightReady, setRoundWeightReady] = useState(false);
  const editingRound = name === "unitUpdate" && context.unit?.unitKind === "VOTING_ROUND";
  const computingSelf = name === "resultCompute" && context.unit?.unitKind === "VOTING_ROUND";
  const roundDatesLocked = editingRound && typeof savedBody.roundState === "string" && savedBody.roundState !== "DRAFT";
  const rosterLocked = name === "entryRoster" && (savedBody.rosterFrozen === true || savedBody._roundRosterFrozen === true);
  const [moveParents, setMoveParents] = useState<Unit[]>([]);
  const [moveParentsError, setMoveParentsError] = useState<unknown>();
  useEffect(() => {
    setMoveParents([]); setMoveParentsError(undefined);
    if (name !== "unitMove" || !context.unit) return;
    const controller = new AbortController();
    const targetKind = context.unit.unitKind === "VOTING_ROUND" ? "STAGE" : "COMPETITION";
    const parent = targetKind === "STAGE" ? context.unit.registrationScopeId : undefined;
    void (async () => {
      const rows: Unit[] = []; let cursor = "";
      do {
        const page = await eventRequest<Page>(operation("units"), {
          ...(parent ? { parentUnitId: parent } : { organizerId: context.organizerId, serviceProductId: context.serviceProductId }),
          limit: 100, ...(cursor ? { cursor } : {}),
        }, undefined, controller.signal);
        rows.push(...page.items as unknown as Unit[]); cursor = String(page.nextCursor || "");
        if (rows.length > 1000) throw new Error("Too many move destinations");
      } while (cursor);
      if (!controller.signal.aborted) setMoveParents(rows.filter(row => row.unitKind === targetKind && row.unitId !== context.unit?.parentUnitId));
    })().catch(e => { if (!controller.signal.aborted) setMoveParentsError(e); });
    return () => controller.abort();
  }, [name, context.unit?.unitId, context.unit?.parentUnitId, context.unit?.unitKind, context.unit?.registrationScopeId, context.organizerId, context.serviceProductId]);
  const unitLabels = unitDetailsLabels(context.unit?.unitKind, lang);
  const fieldLabels: Record<string, string> | undefined = name === "unitUpdate" ? { title: unitLabels.name } : name === "groupCreate" ? { name: w("اسم الفريق", "Team name"), logoMediaId: w("شعار الفريق", "Team logo") } : undefined;
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoStatus, setLogoStatus] = useState({ busy: false, blocked: false });
  const [uncertain, setUncertain] = useState(false);
  useEffect(() => { onLock?.(busy || uncertain || logoStatus.busy); return () => onLock?.(false); }, [busy, uncertain, logoStatus.busy, onLock]);
  const [error, setError] = useState<unknown>();
  const [teamVersionNeedsRefresh, setTeamVersionNeedsRefresh] = useState(false);
  const [validation, setValidation] = useState<FieldValidation>({ attempt: 0, issues: [], focusPath: "" });
  const [result, setResult] = useState<RecordValue | null>(null);
  const [reload, setReload] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [publicationVersion, setPublicationVersion] = useState<number | null>(null);
  const [publicationRevision, setPublicationRevision] = useState(0);
  const [publicationScopeReady, setPublicationScopeReady] = useState(true);
  const publishing = name === "unitLifecycle" && body.status === "PUBLISHED";
  const publicationBlocked = publishing && (!publicationScopeReady || publicationVersion !== body.expectedVersion || body.publicationRoundId === "");
  const pending = useRef<{ payload: string; id: string } | null>(null);
  const running = useRef(false);
  const form = useRef<HTMLFormElement>(null);
  const focusedRequest = useRef<number | undefined>(undefined);
  const queryKey = JSON.stringify(query);
  const staleTeamVersion = name === "groupCreate" && teamVersionNeedsRefresh && !uncertain;
  const queryComplete = validate(querySchema(op), query, true).length === 0;
  const destructive = [
    "unitDeleteDraft",
    "unitLifecycle",
    "entryLifecycle",
    "roundLifecycle",
    "resultPublish",
    "resultUnpublish",
    "adminRevoke",
    "roleRevoke",
    "groupMember",
    "workspaceProvision",
    "audienceGrant",
  ].includes(name);
  useEffect(() => {
    if (uncertain) return;
    const controller = new AbortController();
    setReady(false);
    setRoundWeightReady(false);
    setError(null);
    setTeamVersionNeedsRefresh(false);
    setBody({});
    setValidation({ attempt: 0, issues: [], focusPath: "" });
    setConfirmed(false);
    if (validate(querySchema(op), query, true).length)
      return () => controller.abort();
    const timer = setTimeout(() => {
      void prepare(op, query, context, actorId, controller.signal)
        .then((value) => {
          if (!controller.signal.aborted) {
            setBody({ ...value, ...fixedBody });
            setSavedBody(value);
            setRoundWeight(typeof value.roundWeightBps === "number" ? String(value.roundWeightBps / 100) : "");
            setReady(true);
          }
        })
        .catch((e) => {
          if (!controller.signal.aborted) setError(e);
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // Configuration is pinned until an explicit reload; a failed request retains its idempotency key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, name, reload, context.unit?.version, context.workspaceVersion]);
  useEffect(() => {
    if (!ready || !focusRequest || focusedRequest.current === focusRequest.attempt) return;
    if (editingRound && focusRequest.field === "roundWeightBps" && !roundWeightReady) return;
    focusedRequest.current = focusRequest.attempt;
    setValidation(previous => ({ attempt: previous.attempt + 1, issues: [focusRequest.field], focusPath: focusRequest.field }));
  }, [ready, focusRequest, editingRound, roundWeightReady]);
  useLayoutEffect(() => {
    if (!validation.attempt || !validation.focusPath) return;
      const fields = [...(form.current?.querySelectorAll<HTMLElement>("[data-field-path]") || [])];
      const target = fields.find(field => field.dataset.fieldPath === validation.focusPath) ||
        fields.find(field => field.dataset.fieldPath?.startsWith(validation.focusPath + ".")) ||
        [...fields].reverse().find(field => validation.focusPath.startsWith((field.dataset.fieldPath || "") + "."));
      if (!target) return;
      for (let ancestor: HTMLElement | null = target; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
      }
      const controls = target.querySelectorAll<HTMLElement>("input:not([type=hidden]):not([aria-hidden=true]), select, textarea, button");
      const visible = [...controls].find(control => !control.closest("[hidden]") && control.getClientRects().length > 0);
      visible?.focus();
      visible?.scrollIntoView({ block: "center", behavior: "instant" });
  }, [validation.attempt, validation.focusPath]);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (context.readOnly || !ready || running.current || staleTeamVersion || logoStatus.busy || rosterLocked || publicationBlocked || (destructive && !confirmed)) return;
    const draft = clean(op.body!, {
      ...body,
      ...fixedBody,
      ...(editingRound && !roundWeightLocked && roundWeight ? { roundWeightBps: percentageBasisPoints(roundWeight) } : {}),
      requestId: "pending",
    }) as RecordValue;
    if (roundDatesLocked) { delete draft.startsAtEpochMillis; delete draft.endsAtEpochMillis; }
    if (name === "unitUpdate") for (const key of hiddenUnitMetadataFields(context.unit?.unitKind)) delete draft[key];
    if (editingRound && roundWeightLocked) delete draft.roundWeightBps;
    const problems = [
      ...(name === "groupCreate" && logoStatus.blocked ? ["logoMediaId"] : []),
      ...validate(op.body!, draft, true),
      ...(editingRound && (!roundWeightValid || !roundWeightLocked && percentageBasisPoints(roundWeight) === null) ? ["roundWeightBps"] : []),
      ...policyIssues(draft),
    ];
    setValidation(previous => ({ attempt: previous.attempt + 1, issues: problems, focusPath: problems[0] || "" }));
    if (problems.length) return;
    const payload = JSON.stringify({ query, body: draft });
    if (uncertain && pending.current?.payload !== payload) return;
    if (pending.current?.payload !== payload)
      pending.current = { payload, id: crypto.randomUUID() };
    running.current = true;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const saved = await eventRequest(op, query, {
        ...draft,
        requestId: pending.current.id,
      });
      pending.current = null;
      setUncertain(false);
      setResult(saved);
      setReady(false);
      setConfirmed(false);
      onChanged(saved);
    } catch (e) {
      const unknown = !(e instanceof ApiError) || e.status >= 500;
      setUncertain(unknown);
      if (!unknown) pending.current = null;
      setError(e);
      if (name === "groupCreate" && !unknown) {
        if (e instanceof ApiError && e.status === 409 && ["EVENT_VERSION_CONFLICT", "EVENT_UNIT_VERSION_CONFLICT"].includes(e.code)) setTeamVersionNeedsRefresh(true);
        const messages = teamFieldErrors(e);
        const issues = Object.keys(messages);
        if (issues.length) setValidation(previous => ({ attempt: previous.attempt + 1, issues, focusPath: issues[0], messages }));
      }
      if (publishing && e instanceof ApiError && e.code === "EVENT_NOT_READY") {
        setPublicationVersion(null); setPublicationRevision(n => n + 1);
      }
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  async function refreshTeamVersion() {
    if (!staleTeamVersion || running.current || context.readOnly) return;
    running.current = true; setBusy(true);
    try {
      const current = await prepare(op, query, context, actorId, AbortSignal.timeout(15000));
      setBody(previous => ({ ...previous, expectedVersion: current.expectedVersion }));
      pending.current = null; setError(null); setTeamVersionNeedsRefresh(false);
    } catch (failure) { setError(failure); }
    finally { running.current = false; setBusy(false); }
  }
  const internal = [
    ...Object.keys(fixedBody || {}),
    "requestId",
    "roundWeightBps",
    "expectedVersion",
    "expectedParentVersion",
    "expectedEntryVersion",
    "expectedGroupVersion",
    "expectedScopeVersion",
    "expectedTargetVersion",
    "expectedTargetParentVersion",
    "expectedConfigVersion",
  ];
  if (name === "unitCreate")
    internal.push("unitId", "organizerId", "serviceProductId", "parentUnitId");
  if (name === "entryCreate") internal.push("entryId");
  if (name === "groupCreate") internal.push("partyId", "initialMembers", "affiliatedPartyId");
  if (computingSelf) internal.push("sourceSnapshotRefs");
  const saveLabels: Record<string, [string, string]> = {
    votingPolicy: ["حفظ سياسة التصويت", "Save voting policy"], evaluationForm: ["حفظ نموذج التقييم", "Save evaluation form"],
    resultPolicy: ["حفظ قواعد النتيجة", "Save result rules"], roundSchedule: ["حفظ طريقة التشغيل", "Save round schedule"],
    entryUpdate: ["حفظ بيانات المشاركة", "Save entry details"],
  };
  const lifecycle = name === "unitLifecycle";
  const lifecycleActions: Record<string, [string, string]> = {
    DRAFT: ["حفظ كمسودة", "Save as draft"], PUBLISHED: ["تأكيد النشر", "Confirm publishing"], ARCHIVED: ["تأكيد الأرشفة", "Confirm archiving"],
  };
  const actionTitle = lifecycle ? lifecycleActions[String(body.status)] ? w(...lifecycleActions[String(body.status)]) : w("تطبيق الحالة", "Apply status") : name === "unitUpdate" ? unitLabels.save : saveLabels[name] ? w(...saveLabels[name]) : title;
  const intros: Record<string, [string, string]> = {
    unitLifecycle: ["اختر الحالة الجديدة، ثم راجع التغيير وأكّد تنفيذه.", "Choose the new status, then review and confirm the change."],
    groupCreate: ["أنشئ الفريق، ثم ادعُ أعضاءه. بعد قبول الدعوات، اختر الفريق لإدراجه في الجولة.", "Create the team and invite its members. After they accept, select the team for round admission."],
    roundEntry: ["اختر الفريق؛ تُسجّل مشاركته وتُدرج في الجولة معاً. يلزم قبول الدعوة من عضو واحد على الأقل؛ الدعوات المعلقة لا تُحتسب.", "Choose a team to register and admit it together. At least one member must have accepted their invitation; pending invitations do not count."],
    votingPolicy: ["حدّد من يصوّت وكيف تُحسب مشاركته في النتيجة.", "Choose who can vote and how their input contributes to the result."],
    evaluationForm: ["اختر طريقة الحساب وأضف المعايير: نقاط لكل معيار أو أوزان نسبية مجموعها 100%.", "Choose the scoring method and add criteria: points per criterion or percentage weights totaling 100%."],
    resultPolicy: ["اختر المصادر، ثم حدّد الأوزان وقواعد التعادل والغياب.", "Choose sources, then set weights and rules for ties and missing entries."],
    roundSchedule: ["اختر التحكم اليدوي أو مواعيد التشغيل التلقائي.", "Choose manual control or set the automatic opening and closing times."],
  };
  const heading = <>
      <div className="panel-title">
        <h3>{name === "unitUpdate" ? unitLabels.heading : title}</h3>
        {name !== "groupCreate" && <button
          type="button"
          className="text-button"
          disabled={busy || uncertain}
          onClick={() => {
            pending.current = null;
            setReload((x) => x + 1);
          }}
        >
          {w("تحميل القيم الحالية", "Load current values")}
        </button>}
      </div>
      <p className="event-form-intro">{intros[name] ? w(...intros[name]) : w("راجع التفاصيل ثم احفظ التغيير.", "Review the details, then save the change.")} <span>{w("الحقول بعلامة * مطلوبة.", "Fields marked * are required.")}</span></p>
      {context.unit && <div className="event-target-summary"><span>{w("ضمن", "Within")}</span><strong>{context.unit.title}</strong>{name !== "groupCreate" && <span>{label(context.unit.unitKind, lang)}</span>}{lifecycle && <span className="event-lifecycle-current">{w("الحالة الحالية:", "Current status:")} <strong>{label(context.unit.status, lang)}</strong></span>}</div>}
    </>;
  const updateBody = (value: RecordValue) => {
    setBody(value);
    setConfirmed(false);
    const messages = Object.fromEntries(Object.entries(validation.messages || {}).filter(([field]) => value[field] === body[field]));
    setValidation(previous => ({ ...previous, issues: Object.keys(messages), messages }));
    if (validation.messages && Object.keys(messages).length === 0) setError(null);
  };
  return (
    <ValidatedForm
      ref={form}
      className={`panel admin-pad admin-form command-form${lifecycle ? " event-lifecycle-form" : name === "unitUpdate" ? " event-unit-details" : name === "groupCreate" ? " event-team-form" : ""}`}
      noValidate
      pending={logoStatus.busy}
      aria-busy={busy || logoStatus.busy}
      onSubmit={(e) => void submit(e)}
    >
      {name === "unitUpdate" ? <header className="event-details-header">
        <div>
          <div className="event-details-title"><h3>{unitLabels.heading}</h3>{context.unit && <span className={`event-state event-state-${context.unit.status.toLowerCase()}`}>{label(context.unit.status, lang)}</span>}</div>
          <p>{w("الاسم والوصف والفترة الزمنية.", "Name, description and schedule.")}</p>
        </div>
        <button type="button" className="text-button event-details-reload" aria-label={w("استعادة القيم المحفوظة", "Restore saved values")} disabled={busy || uncertain} onClick={() => { pending.current = null; setReload(x => x + 1); }}>
          <ArrowPathIcon aria-hidden="true" />{w("استعادة القيم", "Restore values")}
        </button>
      </header> : name === "groupCreate" ? <header className="event-team-context">{heading}</header> : heading}
      {rosterLocked && <p className="info-box">{w("ثُبّت تشكيل هذه المشاركة بعد اعتماد الجولة؛ لا يمكن تغيير الأعضاء أو المشرف هنا.", "This entry roster was frozen when the round was made ready. Its members and supervisor cannot be changed here.")}</p>}
      {computingSelf && <p className="info-box">{w("تُحتسب نتيجة هذه الجولة من بطاقاتها المختومة مباشرةً؛ لا تحتاج إلى لقطات مصادر خارجية.", "This round is computed directly from its sealed ballots; external source snapshots are not needed.")}</p>}
      {name === "unitMove" && <ErrorNotice error={moveParentsError} operation="read" />}
      <fieldset disabled={busy || uncertain || context.readOnly || rosterLocked}>
        {name === "roleGrant" && context.unit && <RoleAccountPicker key={context.unit.unitId} unitId={context.unit.unitId} disabled={busy || uncertain || !!context.readOnly} value={String(query.individualId || "")} onChange={id => setQuery(current => { const next = { ...current }; if (id) next.individualId = id; else delete next.individualId; return next; })} />}
        <Fields
          schema={querySchema(op)}
          value={query}
          onChange={setQuery}
          context={name === "unitMove" ? { ...context, units: moveParents } : context}
          hidden={[...boundFields(op, context), ...Object.keys(fixedQuery || {}), ...(name === "roleGrant" ? ["individualId"] : [])]}
        />
        {!ready && !error && !result && (
          <p className="muted" role="status">
            {w(
              queryComplete ? "جارٍ تحميل الإعدادات الحالية…" : "اختر العنصر المطلوب أعلاه لإظهار إعداداته.",
              queryComplete ? "Loading the current settings…" : "Choose the target above to display its settings.",
            )}
          </p>
        )}
        {ready && (
          <>
            {lifecycle ? <UnitLifecycleFields schema={op.body!} value={body} validation={validation} onChange={value => {
              if (value.status !== body.status) setPublicationVersion(null);
              if (value.status !== "PUBLISHED") delete value.publicationRoundId;
              setBody(value); setConfirmed(false); setError(null); setValidation(previous => ({ ...previous, issues: [] }));
            }} /> : name === "unitUpdate" ? <UnitDetailsFields schema={op.body!} value={body} savedValues={savedBody} onChange={updateBody} context={context} hidden={internal} validation={validation} fieldLabels={fieldLabels} datesLocked={roundDatesLocked}>
              {editingRound && <RoundWeightField scopeUnitId={context.unit!.unitId} roundId={context.unit!.unitId} value={roundWeight} onChange={setRoundWeight} onValidityChange={setRoundWeightValid} onLockChange={setRoundWeightLocked} onReadyChange={setRoundWeightReady} disabled={busy || context.readOnly} />}
            </UnitDetailsFields> : <Fields
              schema={op.body!}
              value={body}
              onChange={updateBody}
              context={context}
              hidden={internal}
              validation={validation}
              fieldLabels={fieldLabels}
              fieldOverrides={name === "groupCreate" && context.unit ? { logoMediaId: <TeamLogoUpload key={context.unit.unitId} unitId={context.unit.unitId}
                value={typeof body.logoMediaId === "string" ? body.logoMediaId : undefined} onStatusChange={setLogoStatus}
                validationError={validation.messages?.logoMediaId} onChange={id => {
                  setBody(current => { const next = { ...current }; if (id) next.logoMediaId = id; else delete next.logoMediaId; return next; });
                  setValidation(current => { const messages = { ...current.messages }; delete messages.logoMediaId; return { ...current, messages, issues: current.issues.filter(field => field !== "logoMediaId") }; });
                  setError(null);
                }} /> } : undefined}
            />}
            {publishing && context.unit && ["COMPETITION", "STAGE"].includes(context.unit.unitKind) && <PublicationRoundPicker
              unit={context.unit} value={typeof body.publicationRoundId === "string" ? body.publicationRoundId : undefined}
              disabled={busy || uncertain || context.readOnly} onReady={setPublicationScopeReady} onChange={id => {
                const next = { ...body }; if (id === undefined) delete next.publicationRoundId; else next.publicationRoundId = id;
                updateBody(next); setPublicationVersion(null);
              }} />}
            {publishing && <PublicationReadiness context={context} expectedVersion={Number(body.expectedVersion)} revision={publicationRevision} onCheck={setPublicationVersion} onTask={onTask}
              publicationRoundId={typeof body.publicationRoundId === "string" ? body.publicationRoundId : undefined} />}
            {name === "groupCreate" && context.unit && <TeamAffiliationPicker unitId={context.unit.unitId} value={String(body.affiliatedPartyId || "")} validationError={validation.messages?.affiliatedPartyId} onChange={value => { const next = { ...body }; if (value) next.affiliatedPartyId = value; else delete next.affiliatedPartyId; updateBody(next); }} />}
            {destructive && (
              <div className="event-confirm-review">
              {lifecycle ? <div className="event-lifecycle-review"><ShieldCheckIcon aria-hidden="true" /><div><strong>{w("مراجعة تغيير الحالة", "Review status change")}</strong><p>{context.unit?.title}</p><div className="event-lifecycle-transition"><span>{label(context.unit?.status || "DRAFT", lang)}</span><ArrowLeftIcon aria-hidden="true" /><strong>{body.status ? label(String(body.status), lang) : w("اختر الحالة الجديدة", "Choose a new status")}</strong></div></div></div> : <p><strong>{w("مراجعة قبل التنفيذ", "Review before applying")}</strong><br />{title}
                {body.targetState || body.state || body.action ? <> · {valueLabel(String(body.targetState || body.state || body.action), "state", lang)}</> : null}
                {context.unit ? <> · {context.unit.title}</> : null}
              </p>}
              <label className="check-label confirmation">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {w(
                  "راجعت العنصر والإجراء وأؤكد تطبيق هذا التغيير.",
                  "I reviewed the target and action and confirm this change.",
                )}
              </label>
              </div>
            )}
            {lifecycle && <div className="event-lifecycle-feedback"><ErrorNotice error={error} /></div>}
            <div className="form-actions">
              <button className="primary" disabled={busy || staleTeamVersion || logoStatus.busy || publicationBlocked || (editingRound && !roundWeightValid) || (destructive && !confirmed)}>
                {busy ? w("جارٍ الحفظ…", "Saving…") : actionTitle}
              </button>
              {(name === "groupCreate" || name === "unitUpdate") && onCancel ? <button type="button" className="secondary" disabled={busy || logoStatus.busy} onClick={onCancel}>{w("إلغاء", "Cancel")}</button> : <small className="muted">
                {w(
                  "تُحفظ التغييرات عند الضغط على الزر.",
                  "Changes are saved when you press the button.",
                )}
              </small>}
            </div>
          </>
        )}
      </fieldset>
      {uncertain && <div className="info-box"><p>{w("لم يتأكد حفظ التغيير بعد. أعد المحاولة بالبيانات نفسها قبل مغادرة هذه الخطوة.", "The save is not confirmed. Retry the same request before leaving this step.")}</p><button type="submit" className="primary" disabled={busy || context.readOnly}>{w("إعادة محاولة الحفظ", "Retry save")}</button></div>}
      {validation.issues.length > 0 && (
        <div className="error-box event-validation-summary" role="alert">
          {name === "groupCreate" ? w("تعذّر إضافة الفريق. صحّح الحقول التالية ثم احفظ مجدداً:", "The team could not be added. Correct these fields and save again:") : w(
            "راجع الحقول التالية ومجموع الأوزان إن وجد:",
            "Review these fields and any weight totals:",
          )}
          <ul>
            {[...new Set(validation.issues)].map((i) => (
              <li key={i}>
                <button type="button" onClick={() => setValidation(previous => ({ ...previous, attempt: previous.attempt + 1, focusPath: i }))}>{i
                  .split(".")
                  .map((k) => fieldLabels?.[k as keyof typeof fieldLabels] ?? label(k, lang))
                  .join(" / ")}</button>{validation.messages?.[i] && <span> — {validation.messages[i]}</span>}
              </li>
            ))}
          </ul>
          {validation.messages && error instanceof ApiError && <details><summary>{w("تفاصيل للدعم", "Support details")}</summary><small dir="ltr">{error.code}</small></details>}
        </div>
      )}
      {(!lifecycle || !ready) && !Object.keys(validation.messages || {}).length ? <ErrorNotice error={error} action={name} /> : null}
      {staleTeamVersion && <div className="info-box"><p>{w("تغيّرت إعدادات الخدمة منذ فتح النموذج. حدّث بيانات الحفظ ثم راجع الفريق واحفظه؛ تبقى المدخلات والشعار كما هي.", "Service settings changed after this form opened. Refresh save details, then review and save the team; entered fields and the logo are preserved.")}</p><button type="button" className="secondary" disabled={busy} onClick={() => void refreshTeamVersion()}>{w("تحديث مع الاحتفاظ بالمدخلات", "Refresh and keep entered fields")}</button></div>}
      {result && name === "resultCompute" ?
        <ResultComputationProgress key={String(result.jobId || "")} jobId={typeof result.jobId === "string" ? result.jobId : ""} onReview={onTask ? () => onTask("structure", "resultSnapshots") : undefined} /> : result && (
        <div className="command-result">
          <p className="success-box" role="status">
            {w("تم تنفيذ الإجراء وحفظه.", "Action completed and saved.")}
          </p>
        </div>
      )}
    </ValidatedForm>
  );
}
